import { useState, MouseEvent } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, IconButton, Stack, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { CreateRoleDto } from '@sr/dto';
import { usePermissionsList } from './permission-picker/hook';
import { PermissionGroups } from './permission-groups';
import { useSnack } from '../../../shared/ui/snack';

type Props = {
  onSave: (dto: CreateRoleDto) => Promise<void>;
  onCancel: () => void;
};

export const RoleAccordionCreate = ({ onSave, onCancel }: Props) => {
  const { list: allPermissions = [] } = usePermissionsList();
  const { showSuccess, showError } = useSnack();

  const [expanded, setExpanded] = useState(true);
  const [title, setTitle] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const togglePermissions = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleSave = async (e: MouseEvent) => {
    e.stopPropagation();
    const dto: CreateRoleDto = {
      title: title.trim(),
      permissions: Array.from(selectedIds),
    };
    try {
      await onSave(dto);
      showSuccess('Роль успешно добавлена');
      setExpanded(false);
    } catch (err) {
      showError('Ошибка создания роли', err);
    }
  };

  const handleCancel = (e: MouseEvent) => {
    e.stopPropagation();
    setExpanded(false);
    onCancel();
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <TextField
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Название роли"
            sx={{ minWidth: 280 }}
          />

          <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" color="success" onClick={handleSave} aria-label="Сохранить" disabled={!title.trim()}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={handleCancel} aria-label="Отменить">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails onClick={(e) => e.stopPropagation()}>
        <PermissionGroups permissions={allPermissions} mode="edit" selectedIds={selectedIds} onToggle={togglePermissions} emptyLabel="Пермиссии не найдены" />
      </AccordionDetails>
    </Accordion>
  );
};
